import { useState } from 'react';
import { Container, Title, Text, Group, Button, Menu, Box, Badge, Center, Stack, Anchor } from '@mantine/core';
import { IconGitBranch, IconChevronDown, IconCheck, IconRocket, IconRefresh } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { flowsApi } from '@/services/flows.api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { captureException } from '@/lib/sentry';
import FlowEditor from '@/features/editor/components/FlowEditor';

export default function FlowEditorPage() {
  const { id } = useParams<{ id: string }>();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  const { data: flow, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['flow', id],
    queryFn: () => flowsApi.getFlow(id!),
  });

  // ── Initial-load gates (only when no data has ever arrived) ──────────────
  //
  // Background refetches (e.g. triggered by useFlowPersistence after a save)
  // can fail while stale data is still present.  Showing a full-screen error
  // in that case would unmount the editor and destroy unsaved work.
  // We therefore only redirect to loading/error screens when `flow` is absent.

  if (!flow) {
    if (isLoading) {
      return <LoadingSpinner message="Loading flow…" />;
    }

    if (isError) {
      const is404 =
        axios.isAxiosError(error) && error.response?.status === 404;

      if (is404) {
        // Expected: missing or other-tenant flow.  Don't report to Sentry.
        return (
          <Center style={{ minHeight: '60vh' }}>
            <Stack align="center" gap="md">
              <Title order={2}>Flow not found</Title>
              <Text c="dimmed" ta="center" maw={420}>
                It may have been deleted, or you may not have access.
              </Text>
              <Anchor component={Link} to="/dashboard">
                Back to Dashboard
              </Anchor>
            </Stack>
          </Center>
        );
      }

      // Unexpected: network failure, 500, etc.  Report to Sentry.
      captureException(error instanceof Error ? error : new Error(String(error)), {
        flowId: id,
      });

      return (
        <Center style={{ minHeight: '60vh' }}>
          <Stack align="center" gap="md">
            <Title order={2}>Couldn't load this flow</Title>
            <Text c="dimmed" ta="center" maw={420}>
              Something went wrong. The error has been reported.
            </Text>
            <Button
              leftSection={<IconRefresh size={16} />}
              variant="default"
              onClick={() => void refetch()}
            >
              Try again
            </Button>
            <Anchor component={Link} to="/dashboard">
              Back to Dashboard
            </Anchor>
          </Stack>
        </Center>
      );
    }
  }

  // ── Happy path ─────────────────────────────────────────────────────────────
  // `flow` is guaranteed to be defined here (either freshly loaded or stale
  // data kept alive while a background refetch is in progress / has failed).

  const versions = flow!.versions || [];
  const currentVersion = selectedVersionId
    ? versions.find((v: any) => v.id === selectedVersionId)
    : versions[0];
  const isCurrentPublished = currentVersion?.id === flow!.publishedVersionId;

  return (
    <Container size="100%" style={{ height: '100vh', padding: 0 }}>
      <div style={{ padding: '1rem', borderBottom: '1px solid #e0e0e0' }}>
        <Group justify="space-between">
          <div>
            <Group gap="sm" align="center">
              <Title order={2}>{flow!.name}</Title>
              {flow!.isTemplate && (
                <Badge variant="light" color="grape" data-testid="template-badge">
                  Template
                </Badge>
              )}
              {currentVersion && (
                <Menu shadow="md" width={220}>
                  <Menu.Target>
                    <Button
                      variant="default"
                      size="xs"
                      leftSection={<IconGitBranch size={14} />}
                      rightSection={<IconChevronDown size={14} />}
                      styles={{
                        root: {
                          backgroundColor: isCurrentPublished ? '#e6fcf5' : '#f1f3f5',
                          borderColor: isCurrentPublished ? '#38d9a9' : '#dee2e6',
                          borderRadius: 4,
                          fontWeight: 500,
                        },
                      }}
                    >
                      v{currentVersion.version || '1'}
                      {isCurrentPublished && ' (published)'}
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Versions</Menu.Label>
                    {versions.map((version: any, index: number) => {
                      const isPublished = version.id === flow!.publishedVersionId;
                      return (
                        <Menu.Item
                          key={version.id}
                          leftSection={version.id === currentVersion.id ? <IconCheck size={14} /> : <Box w={14} />}
                          onClick={() => setSelectedVersionId(version.id)}
                        >
                          <Group justify="space-between" w="100%">
                            <Text size="sm">v{version.version}</Text>
                            <Group gap={4}>
                              {isPublished && (
                                <Badge size="xs" color="green" variant="light" leftSection={<IconRocket size={10} />}>
                                  published
                                </Badge>
                              )}
                              {index === 0 && !isPublished && (
                                <Text size="xs" c="dimmed">latest</Text>
                              )}
                            </Group>
                          </Group>
                        </Menu.Item>
                      );
                    })}
                  </Menu.Dropdown>
                </Menu>
              )}
            </Group>
            <Text size="sm" c="dimmed">
              {flow!.description || 'No description'}
            </Text>
          </div>
          <Button component={Link} to="/dashboard" variant="subtle">
            Back to Dashboard
          </Button>
        </Group>
      </div>

      {/*
        key forces a full remount whenever the flow or version changes.
        This ensures useFlowEditorState's initialFlow loading effect starts
        fresh (initialState === null) so the new version's DSL is always
        loaded, even when the user had unsaved edits on the previous version.
        Unsaved edits are discarded on a version switch — no confirm dialog.
        The post-save refetch does NOT change the key (same flowId and
        currentVersion.id), so the guard that protects canvas edits made
        while a save was in-flight continues to work correctly.
      */}
      <FlowEditor
        key={`${id}:${currentVersion?.id}`}
        flowId={id!}
        flowName={flow!.name}
        initialFlow={currentVersion?.dsl}
        currentVersion={currentVersion}
        versions={versions}
        publishedVersionId={flow!.publishedVersionId}
        isTemplate={flow!.isTemplate}
      />
    </Container>
  );
}
